// Reading text: a lesson body is a column of paragraphs, headings and lists with inline emphasis,
// links and terms. These are the only places a list, an emphasis or an inline term button is
// drawn, so a screen composes them and never writes the markup.
import "./config";
import { Anchor, Text, View, YStack, styled } from "tamagui";
import { buttonType } from "./aria";
import { GestureArea } from "./gestures/GestureArea";
import { Icon } from "./Icon";
import { VisuallyHidden } from "./layout";
import { Body } from "./text";

/** The reading column: a readable measure and an even rhythm between its blocks. */
export const Prose = styled(YStack, { name: "Prose", gap: "3", maxWidth: "62ch" });

/** Prose type is a step larger than UI text: it is the thing being read. */
export const ProseText = styled(Body, {
  name: "ProseText",
  render: "span",
  fontSize: "5",
  lineHeight: "5",
});

export const ProseParagraph = styled(ProseText, { name: "ProseParagraph", render: "p" });

export const ProseHeading = styled(Text, {
  name: "ProseHeading",
  fontFamily: "heading",
  fontWeight: "600",
  color: "text",
  marginTop: "1",
  variants: {
    level: {
      3: { fontSize: "6", lineHeight: "6" },
      4: { fontSize: "5", lineHeight: "5" },
    },
  } as const,
});

const ListFrame = styled(YStack, {
  name: "ProseList",
  gap: "2",
  margin: 0,
  paddingLeft: "5",
  paddingRight: 0,
  paddingVertical: 0,
});

export interface ProseListProps {
  ordered?: boolean;
  /** The number an ordered list starts at. */
  start?: number | undefined;
  children: React.ReactNode;
}

export function ProseList({ ordered = false, start, children }: ProseListProps): React.JSX.Element {
  const style: React.CSSProperties = { listStyleType: ordered ? "decimal" : "disc" };
  // `start` is a style key to Tamagui, so the number an ordered list begins at is set on its counter.
  if (ordered && start !== undefined) style.counterReset = `list-item ${String(start - 1)}`;
  return (
    <ListFrame render={ordered ? "ol" : "ul"} style={style}>
      {children}
    </ListFrame>
  );
}

const ItemFrame = styled(View, { name: "ProseItem", render: "li" });

/** A list item; it stays a real list item so the browser draws its marker. */
export function ProseItem({ children }: { children: React.ReactNode }): React.JSX.Element {
  return <ItemFrame style={{ display: "list-item" }}>{children}</ItemFrame>;
}

export const Strong = styled(Text, { name: "Strong", render: "strong", fontWeight: "700" });
export const Emphasis = styled(Text, { name: "Emphasis", render: "em", fontStyle: "italic" });

export interface ProseLinkProps {
  href: string;
  /** What a screen reader adds after the link text: "(opens in a new window)". */
  externalNote: string;
  testId?: string | undefined;
  children: React.ReactNode;
}

/** A link out of the app: new window, never prefetched, and it says so to a screen reader. */
export function ProseLink({
  href,
  externalNote,
  testId,
  children,
}: ProseLinkProps): React.JSX.Element {
  return (
    <Anchor
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      fontFamily="body"
      fontSize="inherit"
      lineHeight="inherit"
      fontWeight="600"
      color="accent"
      cursor="pointer"
      textDecorationLine="underline"
      data-testid={testId}
      style={{ textUnderlineOffset: 3 }}
    >
      {children}
      <Icon name="arrow-up-right" size={12} />
      <VisuallyHidden> {externalNote}</VisuallyHidden>
    </Anchor>
  );
}

const TermFrame = styled(Text, {
  name: "TermButton",
  render: "button",
  ...buttonType(),
  cursor: "pointer",
  color: "inherit",
  padding: 0,
  borderWidth: 0,
  backgroundColor: "transparent",
  borderRadius: 3,
  textDecorationLine: "underline",
  textDecorationStyle: "dotted",
  userSelect: "none",
  outlineWidth: { default: 0, "focus-visible": 3 },
  outlineStyle: "solid",
  outlineColor: "accent",
  outlineOffset: 2,
});

export interface TermButtonProps {
  children: React.ReactNode;
  /** A tap, Enter, Space, hover or focus: show what the term means. A tap waits out a possible double-tap. */
  onOpen: () => void;
  /** Hover or focus ended. */
  onClose: () => void;
  /** Double-tap: keep the explanation open (or let it go); without it a tap opens at once. */
  onPin?: (() => void) | undefined;
  /** Long press, right-click or the menu key: the term's menu. */
  onMenu: () => void;
  testId: string;
}

/**
 * A word in running text that explains itself. Tap, Enter and Space (a click), hover and focus open
 * it; the double-tap and the long-press menu are gesture shortcuts around it, both with a twin.
 */
export function TermButton({
  children,
  onOpen,
  onClose,
  onPin,
  onMenu,
  testId,
}: TermButtonProps): React.JSX.Element {
  return (
    <GestureArea
      render="span"
      display="inline"
      onTap={onOpen}
      onDoubleTap={onPin}
      onLongPress={onMenu}
    >
      <TermFrame
        data-testid={testId}
        aria-haspopup="dialog"
        style={{ font: "inherit" }}
        onFocus={onOpen}
        onBlur={onClose}
        onMouseEnter={onOpen}
        onMouseLeave={onClose}
      >
        {children}
        <Icon name="info" size={11} tone="accent" />
      </TermFrame>
    </GestureArea>
  );
}
